import { computeOrderCustomFees, Decimal, type OrderCustomFee } from '@mlm/shared';
import type { SelectedFeeRow } from './use-pos-sale';

/**
 * What the customer owes, worked out the way the server works it out.
 *
 * The till already gets the goods total (after membership / rank / stockist
 * line pricing) from `POST /pos/checkout/preview`. The cashier discount and
 * the custom fees are layered on here, client-side, so typing a discount does
 * not cost a round-trip per keystroke. The arithmetic mirrors
 * `createOrderInTx` line for line, in Decimal:
 *
 *   discount = min(goods, goods × pct/100 + amount), never below 0, CASH only
 *   feeBase  = goods − discount
 *   fees     = computeOrderCustomFees(feeBase, …)   (the server's own function)
 *   due      = feeBase + fees
 *
 * `due` matters beyond display: a card tender must EQUAL the order total, so
 * it is what the till sends as the tendered amount for an exact method. It is
 * rounded to 4 places exactly as the `numeric(18,4)` column rounds it.
 */
export type CheckoutTotals = {
  /** Goods after line discounts, before the cashier discount. */
  goods: Decimal;
  cashierDiscount: Decimal;
  feeLines: OrderCustomFee[];
  feeTotal: Decimal;
  due: Decimal;
  /** `due` as the string to tender for an exact (card) method. */
  dueFixed: string;
};

export function checkoutTotals(args: {
  /** `total` from the preview: goods after line discounts. */
  goodsTotal: string;
  currency: 'cash' | 'gc' | 'coin';
  discountPct: string;
  discountAmount: string;
  fees: SelectedFeeRow[];
}): CheckoutTotals {
  const goods = new Decimal(args.goodsTotal);
  const cash = args.currency === 'cash';
  // A cashier discount and a fee are both peso figures: a GC / coin order
  // carries neither, and the server ignores or rejects them there.
  const pct = cash ? toDecimal(args.discountPct) : new Decimal(0);
  const amount = cash ? toDecimal(args.discountAmount) : new Decimal(0);
  let cashierDiscount = goods.mul(pct.div(100)).plus(amount);
  if (cashierDiscount.gt(goods)) cashierDiscount = goods;
  if (cashierDiscount.lt(0)) cashierDiscount = new Decimal(0);

  const feeBase = goods.minus(cashierDiscount);
  const priced = cash
    ? computeOrderCustomFees(feeBase, args.fees)
    : { lines: [] as OrderCustomFee[], total: new Decimal(0) };
  const due = feeBase.plus(priced.total);
  return {
    goods,
    cashierDiscount,
    feeLines: priced.lines,
    feeTotal: priced.total,
    due,
    dueFixed: due.toFixed(4),
  };
}

/** A typed field as a Decimal; empty or half-typed ("", ".") counts as 0. */
function toDecimal(raw: string): Decimal {
  const n = Number(raw);
  return Number.isFinite(n) && raw.trim() !== '' ? new Decimal(raw.trim()) : new Decimal(0);
}
